import React from 'react';
import { Shield, User } from 'lucide-react';
import { formatDate } from './providerUtils';

/** Purpose: Renders the Grant HIPAA Permission modal dialog. Inputs: props object. Output: React render output (JSX). */
function GrantPermissionModal({
  open,
  onClose,
  grantForm,
  setGrantForm,
  grantError,
  isSavingPermission,
  onSubmit,
  providers,
  records,
  onToggleGrantRecord
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Grant HIPAA Permission</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">
            Close
          </button>
        </div>

        {grantError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
            {grantError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Provider *</label>
            <select
              value={grantForm.providerId}
              onChange={(event) => setGrantForm((prev) => ({ ...prev, providerId: event.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            >
              <option value="">Select provider</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} - {provider.organization}
                </option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Purpose *"
            value={grantForm.purpose}
            onChange={(event) => setGrantForm((prev) => ({ ...prev, purpose: event.target.value }))}
            className="px-3 py-2 border border-slate-300 rounded-lg md:col-span-2"
          />
          <input
            type="date"
            value={grantForm.expiryDate}
            onChange={(event) => setGrantForm((prev) => ({ ...prev, expiryDate: event.target.value }))}
            min={new Date().toISOString().split('T')[0]}
            className="px-3 py-2 border border-slate-300 rounded-lg"
          />
          <div className="px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-700 text-sm">
            Read Only
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
            <input
              type="checkbox"
              checked={grantForm.allRecords}
              onChange={(event) =>
                setGrantForm((prev) => ({
                  ...prev,
                  allRecords: event.target.checked,
                  recordIds: event.target.checked ? [] : prev.recordIds
                }))
              }
            />
            Blanket HIPAA authorization (all current records)
          </label>
          <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
            <User className="w-4 h-4" />
            {grantForm.allRecords
              ? `All records authorized (${records.length} currently available)`
              : `Select Records (${grantForm.recordIds.length} selected)`}
          </p>
          {grantForm.allRecords ? (
            <div className="border border-slate-200 rounded-lg p-3 text-sm text-slate-600 bg-slate-50">
              This authorization applies to all records currently in your account.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
              {records.map((record) => (
                <label key={record.id} className="flex items-start gap-3 px-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={grantForm.recordIds.includes(record.id)}
                    onChange={() => onToggleGrantRecord(record.id)}
                    className="mt-1"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{record.title}</p>
                    <p className="text-xs text-slate-500">
                      {record.type} • {formatDate(record.date)}{record.provider ? ` • ${record.provider}` : ''}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
          Revoking this authorization later will send an explicit provider notification email when provider email is configured.
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSavingPermission}
            className="px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <Shield className="w-4 h-4" />
            {isSavingPermission ? 'Saving...' : 'Grant Permission'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default GrantPermissionModal;
